import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, FunnelChart, Funnel, LabelList,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
import { getSalesHeaders as h } from '../../utils/salesAuth';
function fmt(v) { if (!v && v !== 0) return '$0'; if (v >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M'; if (v >= 1000) return '$' + (v / 1000).toFixed(1) + 'K'; return '$' + Math.round(v).toLocaleString(); }

const STAGE_LABELS = {
  new: 'New Lead', contact_attempted: 'Contact Attempted', contacted: 'Contacted',
  qualified: 'Qualified', site_visit: 'Site Visit', estimate_in_progress: 'Est. In Progress',
  estimate_sent: 'Estimate Sent', follow_up: 'Follow Up', customer_decision: 'Decision',
  deposit_requested: 'Deposit', won: 'Won', lost: 'Lost',
};
const FUNNEL_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#f59e0b', '#f97316', '#ef4444', '#ec4899', '#a855f7', '#14b8a6', '#22c55e', '#16a34a', '#dc2626', '#6b7280'];
const SRC_COLORS = ['#3b82f6', '#f59e0b', '#ef4444', '#22c55e', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6'];
const SVC_COLORS = ['#6366f1', '#3b82f6', '#14b8a6', '#f59e0b', '#ec4899', '#ef4444', '#22c55e', '#f97316', '#8b5cf6', '#a855f7'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,.3)' }}>
      <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 6px', fontWeight: 600 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontSize: 13, margin: '2px 0' }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function ConversionAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/sales/conversion-analytics`, { credentials: 'include', headers: h() })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading conversion analytics...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Failed to load</div>;

  const km = data.key_metrics || {};
  const rev = data.revenue || {};
  const bd = data.breakdowns || {};

  const funnelData = (data.funnel || []).filter(f => f.reached > 0 && f.stage !== 'lost').map((f, i) => ({
    name: STAGE_LABELS[f.stage] || f.stage, value: f.reached, fill: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
  }));

  const sourceData = (bd.by_source || []).map((s, i) => ({
    name: (s.source || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    total: s.total, won: s.won, lost: s.lost, win_rate: s.win_rate,
    quoted: s.quoted, won_value: s.won_value, fill: SRC_COLORS[i % SRC_COLORS.length],
  }));

  const serviceData = (bd.by_service || []).slice(0, 10).map((s, i) => ({
    name: (s.service || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    total: s.total, won: s.won, win_rate: s.win_rate, fill: SVC_COLORS[i % SVC_COLORS.length],
  }));

  const ai = bd.ai_impact || {};

  return (
    <div data-testid="conversion-analytics" style={{ padding: '24px 28px', maxWidth: 1400 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 6px' }}>Conversion Analytics</h1>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px' }}>Leads → Estimates → Deposits → Sold Revenue</p>

      {/* Hero KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
        <KpiCard label="Estimate-to-Sale" value={`${km.estimate_to_sale_pct}%`} color="#4338ca" sub="Main conversion KPI" large />
        <KpiCard label="Lead-to-Sale" value={`${km.lead_to_sale_pct}%`} color="#3b82f6" sub={`${km.total_won} won of ${km.total_leads} leads`} />
        <KpiCard label="Quoted Revenue" value={fmt(rev.quoted_total)} color="#f59e0b" />
        <KpiCard label="Won Revenue" value={fmt(rev.won_value)} color="#16a34a" />
        <KpiCard label="Revenue Conversion" value={`${rev.revenue_conversion_pct}%`} color="#8b5cf6" sub={`${fmt(rev.won_value)} of ${fmt(rev.quoted_total)}`} />
        <KpiCard label="Open Pipeline" value={fmt(rev.open_value)} color="#f97316" sub={`${km.total_open} opportunities`} />
      </div>

      {/* Conversion Funnel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="Conversion Funnel" testId="chart-conversion-funnel">
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={360}>
              <FunnelChart>
                <Tooltip content={<CustomTooltip />} />
                <Funnel dataKey="value" data={funnelData} isAnimationActive>
                  <LabelList position="right" fill="#475569" stroke="none" fontSize={11} dataKey="name" />
                  <LabelList position="center" fill="#fff" stroke="none" fontSize={13} fontWeight="bold" dataKey="value" />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>

        {/* Revenue Waterfall */}
        <ChartCard title="Quoted → Won Revenue" testId="chart-revenue">
          <div style={{ padding: 20 }}>
            <div style={{ position: 'relative', height: 40, background: '#f1f5f9', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, background: '#16a34a', width: `${rev.revenue_conversion_pct || 0}%`, borderRadius: 8, transition: 'width 1s' }} />
              <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                {fmt(rev.won_value)} of {fmt(rev.quoted_total)} ({rev.revenue_conversion_pct}%)
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 12 }}>
              <div style={{ textAlign: 'center', padding: 12, background: '#f0fdf4', borderRadius: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#16a34a' }}>{fmt(rev.won_value)}</div>
                <div style={{ color: '#64748b', marginTop: 2 }}>Won</div>
              </div>
              <div style={{ textAlign: 'center', padding: 12, background: '#fff7ed', borderRadius: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#f97316' }}>{fmt(rev.open_value)}</div>
                <div style={{ color: '#64748b', marginTop: 2 }}>Open</div>
              </div>
              <div style={{ textAlign: 'center', padding: 12, background: '#fef2f2', borderRadius: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>{fmt(rev.lost_value)}</div>
                <div style={{ color: '#64748b', marginTop: 2 }}>Lost</div>
              </div>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Source + Service Breakdowns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="Conversion by Lead Source" testId="chart-source-conversion">
          {sourceData.length > 0 ? (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    {['Source', 'Total', 'Won', 'Lost', 'Win Rate', 'Won $'].map(h => (
                      <th key={h} style={{ textAlign: h === 'Source' ? 'left' : 'right', padding: '8px 8px', color: '#475569', fontWeight: 600, fontSize: 11 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sourceData.map((s, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px', color: '#1e293b', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: s.fill, flexShrink: 0 }} />{s.name}
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px', color: '#64748b' }}>{s.total}</td>
                      <td style={{ textAlign: 'right', padding: '8px', color: '#16a34a', fontWeight: 600 }}>{s.won}</td>
                      <td style={{ textAlign: 'right', padding: '8px', color: '#dc2626' }}>{s.lost}</td>
                      <td style={{ textAlign: 'right', padding: '8px' }}>
                        <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                          background: s.win_rate >= 50 ? '#dcfce7' : s.win_rate > 0 ? '#fef9c3' : '#f8fafc',
                          color: s.win_rate >= 50 ? '#16a34a' : s.win_rate > 0 ? '#ca8a04' : '#94a3b8',
                        }}>{s.win_rate}%</span>
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px', color: '#1e293b', fontWeight: 500 }}>{fmt(s.won_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty />}
        </ChartCard>

        <ChartCard title="Conversion by Service" testId="chart-service-conversion">
          {serviceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={serviceData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" fontSize={10} tick={{ fill: '#64748b' }} angle={-25} textAnchor="end" height={60} />
                <YAxis fontSize={11} tick={{ fill: '#64748b' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="total" name="Total" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="won" name="Won" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
      </div>

      {/* AI Impact */}
      {ai.ai_assisted && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <ChartCard title="AI-Assisted vs Non-AI Performance" testId="chart-ai-impact">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: 10 }}>
              <div style={{ textAlign: 'center', padding: 16, background: '#eef2ff', borderRadius: 10 }}>
                <div style={{ fontSize: 10, color: '#4338ca', fontWeight: 700, marginBottom: 4 }}>AI-ASSISTED</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#4338ca' }}>{ai.ai_assisted.win_rate}%</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Win Rate ({ai.ai_assisted.won}/{ai.ai_assisted.total})</div>
              </div>
              <div style={{ textAlign: 'center', padding: 16, background: '#f8fafc', borderRadius: 10 }}>
                <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>NON-AI</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#475569' }}>{ai.non_ai.win_rate}%</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Win Rate ({ai.non_ai.won}/{ai.non_ai.total})</div>
              </div>
            </div>
          </ChartCard>

          {/* Monthly trend */}
          <ChartCard title="Monthly Volume" testId="chart-monthly-volume">
            {(bd.by_month || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={bd.by_month} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" fontSize={11} tick={{ fill: '#64748b' }} />
                  <YAxis fontSize={11} tick={{ fill: '#64748b' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="total" name="Total" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="won" name="Won" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="lost" name="Lost" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </ChartCard>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, color, sub, large }) {
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
      style={{ background: '#fff', borderRadius: 10, padding: large ? '20px 22px' : '16px 18px', borderLeft: `4px solid ${color}`, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: large ? 28 : 22, fontWeight: 800, color: '#1e293b', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div data-testid={testId} style={{ background: '#fff', borderRadius: 10, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: '0 0 14px' }}>{title}</h3>
      {children}
    </div>
  );
}

function Empty() {
  return <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No data available yet</div>;
}
